/**
 * A tab opened before a deploy keeps the previous version's entry, which names each page's chunk by
 * its hash. The new deploy no longer has those files, so the first visit to a page that tab had not
 * opened yet fails to import it, and `React.lazy` keeps that failure for good: only a reload brings
 * the new version's entry and names. These helpers recognize that failure and reload the page once.
 */

/**
 * The start of the message of a failed dynamic import, per browser engine, plus the one Vite's
 * preload helper throws when a page's stylesheet is missing.
 */
const CHUNK_ERROR_MESSAGES = [
  'Failed to fetch dynamically imported module', // Chrome and Edge
  'error loading dynamically imported module', // Firefox
  'Importing a module script failed', // Safari
  'Unable to preload CSS', // Vite's preload helper
];

/** The mark of the last reload in this tab: it outlives the reload, not the tab. */
const RELOAD_KEY = 'sl:stale-chunk-reload';

/**
 * How long after a reload another one is refused. Long enough that a chunk still missing after
 * the reload (a broken deploy, an edge still serving the old files) shows the error instead of
 * reloading in a loop; short enough that a later deploy, with the same tab open, reloads it again.
 */
export const RELOAD_WINDOW_MS = 30_000;

/** The browser's reload, behind an object so a test can watch it (jsdom's `location` cannot). */
export const browserPage = {
  reload(): void {
    window.location.reload();
  },
};

function messageOf(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) {
    return null;
  }
  const message = (error as { message?: unknown }).message;
  return typeof message === 'string' ? message : null;
}

function hasChunkMessage(error: unknown): boolean {
  const message = messageOf(error);
  return message !== null && CHUNK_ERROR_MESSAGES.some((start) => message.startsWith(start));
}

/**
 * Whether `error` is a page's chunk (or its stylesheet) that could not be loaded. The cause is
 * looked at one level down too, for a loader that wraps the browser's error in its own.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (hasChunkMessage(error)) {
    return true;
  }
  return typeof error === 'object' && error !== null && hasChunkMessage((error as Error).cause);
}

export interface ReloadOnceOptions {
  storage?: Pick<Storage, 'getItem' | 'setItem'>;
  now?: number;
  reload?: () => void;
  online?: () => boolean;
}

/**
 * Reloads the page unless it was reloaded for this same reason less than `RELOAD_WINDOW_MS` ago,
 * and returns whether it did. It does not reload without a connection (the new version could not
 * be fetched, and the reload would lose the screen for nothing) nor when the mark cannot be read
 * or written (private mode, blocked storage): without it nothing would stop a loop.
 */
export function reloadOnce(options: ReloadOnceOptions = {}): boolean {
  const {
    now = Date.now(),
    reload = () => browserPage.reload(),
    online = () => navigator.onLine,
  } = options;

  if (!online()) {
    return false;
  }

  try {
    const storage = options.storage ?? window.sessionStorage;
    const elapsed = now - Number(storage.getItem(RELOAD_KEY));
    if (elapsed >= 0 && elapsed < RELOAD_WINDOW_MS) {
      return false;
    }
    storage.setItem(RELOAD_KEY, String(now));
  } catch {
    return false;
  }

  reload();
  return true;
}
